import { Box, Button, MenuItem, Select, TextField } from "@mui/material";
import { FC, KeyboardEvent, useState } from "react";
import {
  Controller,
  SubmitHandler,
  useForm,
  UseFormReset,
} from "react-hook-form";
import { useTranslation } from "react-i18next";
import { productStatusOptions } from "../../../../../helpers/selectOptions";
import { inventoryRules } from "../../../../../helpers/validationSchemes";
import {
  Status,
  InventoryData,
  PlanTypes,
} from "../../../../../types/inventoryData";
import { useInventory } from "../../../InventoryProvider";

const AddProductManuallyBatchScan: FC<{
  handleAdd: (
    data: InventoryData,
    dirtyFields: boolean,
    reset?: UseFormReset<InventoryData>
  ) => void;
  chosenValues: InventoryData | null;
}> = ({ handleAdd, chosenValues }) => {
  const handleKeyPress = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key === "Enter") {
      handleSubmit(handleAddProduct);
    }
  };

  const handleAddProduct: SubmitHandler<InventoryData> = (data) => {
    handleAdd(data, isDirty, reset);
  };

  const {
    handleSubmit,
    control,
    reset,
    setValue,
    formState: { isDirty, dirtyFields },
  } = useForm<InventoryData>({
    defaultValues: chosenValues || {
      status: undefined,
      location: undefined,
      note: undefined,
      plan: undefined,
    },
    mode: "onChange",
  });

  const { t } = useTranslation();
  const { plans } = useInventory();

  return (
    <form onSubmit={handleSubmit(handleAddProduct)} style={{ width: "100%" }}>
      <Box
        sx={{
          p: 3,
          height: "100%",
          display: "flex",
          flexDirection: "column",
          justifyContent: "space-between",
          gap: 2,
        }}
      >
        <Controller
          name="status"
          control={control}
          render={({ field, fieldState: { error } }) => {
            const currentValue =
              field.value === undefined ? "KEEP" : field.value;
            return (
              <TextField
                select
                {...field}
                name="status"
                id="status"
                label={`Status`}
                fullWidth
                sx={{ height: "min-Content" }}
                value={currentValue}
                onChange={(e) => {
                  const val =
                    e.target.value == "KEEP" ? undefined : e.target.value;
                  field.onChange(val);
                }}
              >
                <MenuItem value={"KEEP"}>
                  <em>{t("inventory.keepOriginal")}</em>
                </MenuItem>
                {productStatusOptions.map((i) => (
                  <MenuItem key={i.value} value={i.value}>
                    {i.label}
                  </MenuItem>
                ))}
              </TextField>
            );
          }}
        />

        <Controller
          name="location"
          control={control}
          rules={inventoryRules.locationRule}
          render={({ field, fieldState: { error } }) => (
            <TextField
              {...field}
              value={field.value ?? ""}
              error={!!error}
              id="location"
              name="location"
              variant="filled"
              label={t("inventory.location")}
              onChange={(e) => {
                if (e.target.value.trim() == "") {
                  field.onChange(undefined);
                } else {
                  field.onChange(e.target.value);
                }
              }}
              helperText={
                <>
                  {/* Counter */}
                  {`${field?.value?.length || 0}/${
                    inventoryRules.locationRuleMaxLength
                  }`}

                  {/* Error */}
                  {error?.message &&
                    `. ${t(error.message).replace(
                      "{0}",
                      inventoryRules.locationRuleMaxLength.toString()
                    )}`}
                </>
              }
              onKeyDown={(event) => handleKeyPress(event)}
            />
          )}
        />

        <Controller
          name="plan"
          control={control}
          render={({ field, fieldState: { error } }) => {
            const currentValue =
              field.value === undefined
                ? "KEEP"
                : field.value === null
                ? "REMOVE"
                : field.value?.inventoryPlanId ?? "";

            return (
              <TextField
                {...field}
                select
                id="plan"
                name="plan"
                label={`${t("inventory.plan")}`}
                fullWidth
                sx={{ height: "min-Content" }}
                value={currentValue}
                onChange={(e) => {
                  // let val: PlanTypes | undefined | null = undefined;
                  let val = e.target.value;

                  switch (val) {
                    case "KEEP":
                      field.onChange(undefined);
                      break;
                    case "REMOVE":
                      field.onChange(null);
                      break;
                    // setValue("plan", null, {
                    //   shouldDirty: true,
                    //   shouldValidate: true,
                    // });
                    default:
                      const selectedPlan: PlanTypes | undefined = plans.find(
                        (p) => p.inventoryPlanId == parseInt(e.target.value)
                      );
                      field.onChange(selectedPlan);
                      break;
                  }
                }}
              >
                <MenuItem value={"KEEP"}>
                  <em>{t("inventory.keepOriginal")}</em>
                </MenuItem>
                <MenuItem value={"REMOVE"}>
                  <em>{t("labels.none")}</em>
                </MenuItem>
                {plans.map((p) => (
                  <MenuItem key={p.inventoryPlanId} value={p.inventoryPlanId}>
                    {p.companyName}
                  </MenuItem>
                ))}
              </TextField>
            );
          }}
        />

        <Controller
          name="note"
          control={control}
          rules={inventoryRules.noteRule}
          render={({ field, fieldState: { error } }) => (
            <TextField
              {...field}
              value={field.value ?? ""}
              id="note"
              name="note"
              variant="filled"
              label={t("inventory.note")}
              onKeyDown={(event) => handleKeyPress(event)}
              multiline
              error={!!error}
              onChange={(e) => {
                if (e.target.value.trim() == "") {
                  field.onChange(undefined);
                } else {
                  field.onChange(e.target.value);
                }
              }}
              helperText={
                <>
                  {/* Counter */}
                  {`${field?.value?.length || 0}/${
                    inventoryRules.noteRuleMaxLength
                  }`}

                  {/* Error */}
                  {error?.message &&
                    `. ${t(error.message).replace(
                      "{0}",
                      inventoryRules.noteRuleMaxLength.toString()
                    )}`}
                </>
              }
              rows={5}
            />
          )}
        />
        <Button variant="contained" type="submit" size="large">
          {t("inventory.scan")}
        </Button>
      </Box>
    </form>
  );
};

export default AddProductManuallyBatchScan;
